// Bench Candidates — Analytics & Reports page (ADR-107).
//
// A work list over the MySM benchcandidates feed (Americas today; the source
// system filter is where other feeds plug in). Rows carry the upstream claim
// and the Radar picture side by side; actions delegate to radar-api, which
// re-checks eligibility at write time.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import { FaArrowLeft, FaSyncAlt, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Box, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import brandMuiTheme from '../../../styles/brandMuiTheme';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  MainSection,
  BackButton,
} from '../styles';
import { HeaderButton } from '../../common/styles';
import { useAbility } from '../../../contexts/AbilityContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { BENCH_CANDIDATE_SOURCE_SYSTEMS, BENCH_CANDIDATE_STATUSES, BENCH_CANDIDATE_SORT_OPTIONS, benchCandidateSourceSystemLabel } from '../../../constants/benchCandidates';
import { formatDateOnly } from './dates';
import { useBenchCandidatesList, useLatestBenchCandidateRun, useBenchCandidateDetail } from '../../../hooks/useBenchCandidates';
import { benchCandidatesApi } from '../../../services/api/benchCandidatesApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import LatestRunStrip from './LatestRunStrip';
import CandidatesFilterBar from './CandidatesFilterBar';
import CandidatesTable from './CandidatesTable';
import AssignToBenchDialog from './AssignToBenchDialog';
import DismissDialog from './DismissDialog';
import UnassignDialog from './UnassignDialog';
import EditBenchDataModal from '../../ProjectsManagement/ProjectForm/components/Employees/components/EditBenchDataModal';
import { benchApi } from '../../../services/api/benchApi';
import { employeesApi } from '../../../services/api/employeesApi';
import ConfirmationModal from '../../common/ConfirmationModal';
import { LoadMoreButton, SortRow, SortButton, HeaderRight } from './styles';

const PAGE_LIMIT = 50;

// "Use the Americas feed pay rate: 29.97 USD /hour (paid semimonthly)"
const payRateFillLabel = (payRate, sourceSystem) => {
  const period = payRate.period === 'HOUR' ? '/hour' : payRate.period === 'MONTH' ? '/month' : (payRate.paymentType ? payRate.paymentType.toLowerCase() : '');
  const freq = payRate.paymentFrequency ? ` (paid ${payRate.paymentFrequency.toLowerCase()})` : '';
  return `Use the ${benchCandidateSourceSystemLabel(sourceSystem)} feed pay rate: ${payRate.amount} ${payRate.currency || ''} ${period}${freq}`.replace(/\s+/g, ' ').trim();
};

const INITIAL_FILTERS = {
  sourceSystem: BENCH_CANDIDATE_SOURCE_SYSTEMS.AMERICAS,
  benchSource: [],
  // Default population: open work (upstream-removed + dismissed rows are opt-in via the status chips).
  status: [BENCH_CANDIDATE_STATUSES.NEW, BENCH_CANDIDATE_STATUSES.ASSIGNED],
  matchStatus: [],
  clientName: '',
  deliveryManagerName: '',
  search: '',
  daysUntilRollOffMin: '',
  daysUntilRollOffMax: '',
  eligible: '',
  orderBy: 'rollOffDate',
  orderDirection: 'asc',
  limit: PAGE_LIMIT,
};

const BenchCandidates = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const ability = useAbility();
  const { showAlert } = useAlert();

  // Returning from an employee profile: the form forwards location.state back to
  // `returnTo`, so filters, loaded depth and scroll offset come back with it
  // (same round-trip the Bench Report uses with `benchState`).
  const restored = location.state?.candidatesState;
  const mainSectionRef = useRef(null);
  const scrollRestoredRef = useRef(false);

  const permissions = {
    canUpdate: ability.can(ACTIONS.UPDATE, SUBJECTS.BENCH_CANDIDATE),
    canImport: ability.can(ACTIONS.CREATE, SUBJECTS.BENCH_CANDIDATE_IMPORT),
    canEditBenchData: ability.can(ACTIONS.UPDATE, SUBJECTS.BENCH_EMPLOYEE_DATA),
    canTrigger: ability.can(ACTIONS.CREATE, SUBJECTS.BENCH_CANDIDATE_SYNC_RUN),
  };

  const [filters, setFilters] = useState(() => ({ ...INITIAL_FILTERS, ...(restored?.filters || {}), limit: PAGE_LIMIT }));
  const [busyId, setBusyId] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [cancelLeaveTarget, setCancelLeaveTarget] = useState(null);
  // { candidateId, employee } for the shared EditBenchDataModal (same modal as the project page)
  const [benchDataTarget, setBenchDataTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const list = useBenchCandidatesList(filters, { restoreCount: restored?.loadedCount || 0 });
  const { items, pageInfo, asOf, isLoading, isLoadingMore, isRestoring, error, loadMore, refetch, replaceItem } = list;

  // Restore the scroll offset once the previous depth is loaded again.
  useEffect(() => {
    if (!isLoading && !isRestoring && restored?.scrollTop && !scrollRestoredRef.current && mainSectionRef.current) {
      mainSectionRef.current.scrollTop = restored.scrollTop;
      scrollRestoredRef.current = true;
    }
  }, [isLoading, isRestoring, restored]);

  const openEmployee = useCallback((employeeId) => {
    const candidatesState = {
      filters,
      loadedCount: items.length,
      scrollTop: mainSectionRef.current?.scrollTop || 0,
    };
    // ADR-109 — auto open (Edit when the user manages the individual)
    openIndividual(employeeId, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.BENCH_CANDIDATES,
      returnTo: '/reports/bench-candidates',
      returnLabel: 'Bench Candidates',
      state: { candidatesState },
    });
  }, [filters, items.length, openIndividual]);

  const { run: latestRun, isRunning, refetch: refetchRun } = useLatestBenchCandidateRun(filters.sourceSystem, {
    onSettled: () => refetch(),
  });

  const detail = useBenchCandidateDetail();

  const handleFilterChange = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  // Same interaction as BenchEntryLagReport: click a field to sort by it (its default
  // direction from BENCH_CANDIDATE_SORT_OPTIONS), click again to flip.
  const toggleSort = useCallback((field) => {
    if (filters.orderBy === field) {
      handleFilterChange({ orderDirection: filters.orderDirection === 'desc' ? 'asc' : 'desc' });
    } else {
      const option = BENCH_CANDIDATE_SORT_OPTIONS.find((opt) => opt.value === field);
      handleFilterChange({ orderBy: field, orderDirection: option?.defaultDirection || 'asc' });
    }
  }, [filters.orderBy, filters.orderDirection, handleFilterChange]);

  const handleRefreshNow = async () => {
    setTriggering(true);
    try {
      await benchCandidatesApi.triggerRefresh(filters.sourceSystem);
      showAlert?.('Refresh triggered — the list updates when the run finishes', 'success');
      // The strip starts polling as soon as the new run shows up as running.
      setTimeout(refetchRun, 1500);
    } catch (err) {
      showAlert?.(err?.message || 'Failed to trigger refresh', 'error');
    } finally {
      setTriggering(false);
    }
  };

  const reloadRow = async (candidateId) => {
    try {
      const fresh = await benchCandidatesApi.get(candidateId);
      replaceItem(fresh);
    } catch {
      refetch();
    }
  };

  const openAssign = async (candidate) => {
    setAssignTarget(candidate);
    await detail.load(candidate.id);
  };

  const submitAssign = async (body) => {
    setSubmitting(true);
    try {
      const result = await benchCandidatesApi.assignToBench(assignTarget.id, body);
      const warningCount = result?.warnings?.length || 0;
      showAlert?.(`${assignTarget.fullName} assigned to bench${warningCount ? ` (${warningCount} warning${warningCount === 1 ? '' : 's'})` : ''}`, 'success');
      setAssignTarget(null);
      detail.clear();
      await reloadRow(assignTarget.id);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmUnassign = async (body = {}) => {
    const target = unassignTarget;
    setUnassignTarget(null);
    setBusyId(target.id);
    try {
      const result = await benchCandidatesApi.unassign(target.id, body);
      const message = result?.mode === 'cancelled'
        ? `${target.fullName}'s bench allocation cancelled`
        : result?.mode === 'linkage_only'
          ? 'Bench allocation was already ended — link cleared'
          : result?.mode === 'leaving'
            ? `${target.fullName}'s last day on the bench is ${formatDateOnly(result.endDate)} — the leave can be cancelled until then`
            : `${target.fullName}'s bench allocation ended${body.endDate ? ` on ${formatDateOnly(body.endDate)}` : ''}`;
      showAlert?.(message, 'success');
      await reloadRow(target.id);
    } catch (err) {
      showAlert?.(err?.message || 'Failed to remove from bench', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const confirmCancelLeave = async () => {
    const target = cancelLeaveTarget;
    setCancelLeaveTarget(null);
    setBusyId(target.id);
    try {
      const result = await benchCandidatesApi.cancelLeave(target.id);
      showAlert?.(`${target.fullName}'s bench leave cancelled${result?.previousEndDate ? ` (was ${formatDateOnly(result.previousEndDate)})` : ''}`, 'success');
      await reloadRow(target.id);
    } catch (err) {
      showAlert?.(err?.message || 'Failed to cancel bench leave', 'error');
    } finally {
      setBusyId(null);
    }
  };

  // Same modal as the project Individuals menu: load the allocation's bench data, then
  // hand it over in the shape that modal expects. The allocation is the linked one for an
  // assigned row, otherwise the person's active bench allocation.
  const openEditBenchData = async (candidate) => {
    const allocation = candidate.status === 'ASSIGNED' ? candidate.assignedEmployeeProject : candidate.radar?.activeBenchEmployeeProject;
    if (!allocation?.id) {
      return;
    }
    setBusyId(candidate.id);
    try {
      // The person's record carries the direct manager the modal shows as "Current Representative".
      // The candidate detail carries the feed's pay rate as a salary prefill (only for
      // BenchSalary readers — the server decides), offered in the modal as a one-click fill.
      const [benchData, employee, detail] = await Promise.all([
        benchApi.getBenchData(allocation.id),
        candidate.radar?.employeeId ? employeesApi.getEmployeeById(candidate.radar.employeeId).catch(() => null) : Promise.resolve(null),
        benchCandidatesApi.get(candidate.id).catch(() => null),
      ]);
      const payRate = detail?.assignPrefill?.salary || null;
      setBenchDataTarget({
        candidateId: candidate.id,
        employee: { employeeProjectId: allocation.id, fullName: candidate.fullName, directManager: employee?.directManager || null, benchData },
        salarySuggestion: payRate ? { ...payRate, label: payRateFillLabel(payRate, candidate.sourceSystem) } : null,
      });
    } catch (err) {
      showAlert?.(err?.message || 'Failed to load bench data', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const submitDismiss = async (reason) => {
    setSubmitting(true);
    try {
      await benchCandidatesApi.dismiss(dismissTarget.id, reason);
      showAlert?.(`${dismissTarget.fullName} dismissed`, 'success');
      const id = dismissTarget.id;
      setDismissTarget(null);
      await reloadRow(id);
    } catch (err) {
      showAlert?.(err?.message || 'Failed to dismiss', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndismiss = async (candidate) => {
    setBusyId(candidate.id);
    try {
      await benchCandidatesApi.undismiss(candidate.id);
      await reloadRow(candidate.id);
    } catch (err) {
      showAlert?.(err?.message || 'Failed to undismiss', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleImport = async (candidate) => {
    setBusyId(candidate.id);
    try {
      await benchCandidatesApi.requestImport(candidate.id);
      showAlert?.(`Import requested for ${candidate.fullName} — the row updates when the import finishes`, 'success');
      await reloadRow(candidate.id);
      // Poll this row a few times while the import runs (seconds, typically).
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        const fresh = await benchCandidatesApi.get(candidate.id).catch(() => null);
        if (fresh) {
          replaceItem(fresh);
        }
        const stillRunning = fresh?.importRequests?.[0]?.status === 'running';
        if (stillRunning && attempts < 24) {
          setTimeout(poll, 5000);
        }
      };
      setTimeout(poll, 5000);
    } catch (err) {
      const detailMsg = err?.data?.requestId ? ' (an import is already running)' : '';
      showAlert?.(`${err?.message || 'Failed to request import'}${detailMsg}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Bench Candidates</Title>
        </HeaderLeft>
        <HeaderRight>
          <LatestRunStrip run={latestRun} isRunning={isRunning} />
          {permissions.canTrigger && (
            <HeaderButton
              onClick={handleRefreshNow}
              disabled={isRunning || triggering}
              title={isRunning ? 'A refresh is already running' : 'Pull the full bench-candidates feed now'}
            >
              <FaSyncAlt />
              {triggering ? 'Triggering…' : isRunning ? 'Refreshing…' : 'Refresh'}
            </HeaderButton>
          )}
        </HeaderRight>
      </Header>

      <Content>
        <MainSection ref={mainSectionRef}>

          <CandidatesFilterBar filters={filters} onChange={handleFilterChange} />

          {/* totalCount is exact for every filter except "Assignable only" (server post-filter → null): show the loaded count then. */}
          <SortRow>
            <ResultsCount
              count={pageInfo?.totalCount ?? items.length}
              singular={pageInfo?.totalCount == null && pageInfo?.hasNextPage ? 'candidate loaded so far' : 'candidate'}
              plural={pageInfo?.totalCount == null && pageInfo?.hasNextPage ? 'candidates loaded so far' : 'candidates'}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {BENCH_CANDIDATE_SORT_OPTIONS.map((opt) => {
                const active = filters.orderBy === opt.value;
                return (
                  <SortButton
                    key={opt.value}
                    onClick={() => toggleSort(opt.value)}
                    style={active ? { fontWeight: 700 } : {}}
                  >
                    {active && (filters.orderDirection === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />)}
                    {opt.label}
                  </SortButton>
                );
              })}
            </Box>
          </SortRow>

          {(isLoading || isRestoring) && <LoadingSpinner />}
          {!isLoading && error && (
            <EmptyState title="Failed to load bench candidates" description={error?.message || 'Try refreshing the page.'} />
          )}
          {!isLoading && !isRestoring && !error && items.length === 0 && (
            <EmptyState
              title="No bench candidates"
              description={latestRun
                ? 'Nothing matches the current filters.'
                : 'The list fills after the first Americas sync run (daily pipeline) or a manual refresh.'}
            />
          )}
          {!isLoading && !isRestoring && !error && items.length > 0 && (
            <>
              <CandidatesTable
                candidates={items}
                permissions={permissions}
                busyId={busyId}
                onAssign={openAssign}
                onUnassign={setUnassignTarget}
                onCancelLeave={setCancelLeaveTarget}
                onEditBenchData={openEditBenchData}
                onDismiss={setDismissTarget}
                asOf={asOf}
                onUndismiss={handleUndismiss}
                onImport={handleImport}
                onOpenEmployee={openEmployee}
              />
              {pageInfo?.hasNextPage && (
                <LoadMoreButton onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>

      <AssignToBenchDialog
        // Remount per candidate so form state never carries over between rows.
        key={assignTarget?.id || 'none'}
        open={Boolean(assignTarget)}
        candidate={assignTarget}
        detail={detail.candidate}
        loadingDetail={detail.isLoading}
        submitting={submitting}
        onClose={() => { setAssignTarget(null); detail.clear(); }}
        onSubmit={submitAssign}
      />

      <DismissDialog
        open={Boolean(dismissTarget)}
        candidate={dismissTarget}
        submitting={submitting}
        onClose={() => setDismissTarget(null)}
        onSubmit={submitDismiss}
      />

      <ConfirmationModal
        isOpen={Boolean(cancelLeaveTarget)}
        title="Cancel bench leave"
        message={cancelLeaveTarget
          ? `Cancel ${cancelLeaveTarget.fullName}'s bench leave (last day ${formatDateOnly((cancelLeaveTarget.assignedEmployeeProject || cancelLeaveTarget.radar?.activeBenchEmployeeProject)?.endDate)})? The bench allocation continues with no end date — the same as "Cancel Project Leave" on the project.`
          : ''}
        actionButtonText="Cancel leave"
        actionButtonColor="#f59e0b"
        onConfirm={confirmCancelLeave}
        onClose={() => setCancelLeaveTarget(null)}
      />

      {benchDataTarget && (
        <EditBenchDataModal
          key={benchDataTarget.employee.employeeProjectId}
          isOpen
          employee={benchDataTarget.employee}
          salarySuggestion={benchDataTarget.salarySuggestion}
          onClose={() => setBenchDataTarget(null)}
          onSuccess={() => reloadRow(benchDataTarget.candidateId)}
        />
      )}

      <UnassignDialog
        isOpen={Boolean(unassignTarget)}
        candidate={unassignTarget}
        submitting={Boolean(unassignTarget) && busyId === unassignTarget.id}
        onConfirm={confirmUnassign}
        onClose={() => setUnassignTarget(null)}
      />
    </Container>
  );
};

// Brand theme scoped to this page: MUI row actions, dialogs and filter controls
// use MySM navy (COLORS.PRIMARY), matching the header Refresh button.
const BenchCandidatesPage = () => (
  <ThemeProvider theme={brandMuiTheme}>
    <BenchCandidates />
  </ThemeProvider>
);

export default BenchCandidatesPage;
